<template>
    <div class="max-w-6xl mx-auto p-6 bg-slate-800 rounded-xl shadow-lg border border-slate-700">
      <h2 class="text-2xl font-bold mb-6 text-slate-100 flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary-500"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
        JSON Formatter
      </h2>
      
      <div class="space-y-4">
        <textarea 
            class="w-full h-48 px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 resize-y" 
            v-model="jsonInput" 
            placeholder='Paste your JSON here... e.g. {"key": "value"}'>
        </textarea>
        
        <div class="flex flex-wrap gap-2 justify-center sm:justify-start">
            <button class="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors font-medium text-sm flex items-center gap-2" @click="formatJSON">
                Format
            </button>
            <button class="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium text-sm flex items-center gap-2" @click="toggleView" v-if="formattedJson">
                {{ isTreeView ? 'Show Text' : 'Show Tree' }}
            </button>
            <button class="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium text-sm flex items-center gap-2" v-if="isTreeView && formattedJson" @click="showModal = true">
                Fullscreen Tree
            </button>
            <button class="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium text-sm flex items-center gap-2 ml-auto" @click="copyToClipboard(isTreeView ? JSON.stringify(parsedJson, null, 2) : formattedJson)" v-if="formattedJson">
                Copy
            </button>
        </div>

        <div v-if="formattedJson" class="mt-6">
            <pre class="json-output p-4 bg-slate-950 rounded-lg border border-slate-700 text-slate-300 font-mono text-sm overflow-x-auto" v-if="!isTreeView">{{ formattedJson }}</pre>
            <div class="p-4 bg-slate-950 rounded-lg border border-slate-700 text-slate-300 font-mono text-sm overflow-x-auto max-h-[600px]" v-else>
                <json-tree-view :data="parsedJson" />
            </div>
        </div>
      </div>

       <!-- Modal for fullscreen tree view -->
       <div v-if="showModal" class="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/90 p-4">
        <div class="relative w-full max-w-7xl h-[90vh] bg-slate-800 rounded-xl shadow-2xl flex flex-col border border-slate-700">
          <div class="flex items-center justify-between p-4 border-b border-slate-700">
            <h3 class="font-bold text-slate-100">JSON Tree View</h3>
            <button class="text-slate-400 hover:text-white" @click="showModal = false">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="flex-1 overflow-auto p-4 bg-slate-950">
             <json-tree-view :data="parsedJson" />
          </div>
        </div>
      </div>

      <!-- Snackbar -->
      <transition enter-active-class="transition ease-out duration-300" enter-from-class="transform translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100" leave-active-class="transition ease-in duration-200" leave-from-class="transform translate-y-0 opacity-100" leave-to-class="transform translate-y-4 opacity-0">
        <div v-if="showSnackbar" class="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-xl border border-slate-700 flex items-center gap-2 z-[60]">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-400"><polyline points="20 6 9 17 4 12"/></svg>
            Copied to clipboard!
        </div>
      </transition>
    </div>
  </template>
  
  <script lang="ts">
  import { defineComponent, ref } from 'vue';
  import JSONTreeView from './JSONTreeView.vue';
  
  export default defineComponent({
    name: 'JSONFormatter',
    components: {
      'json-tree-view': JSONTreeView
    },
    setup() {
      const jsonInput = ref('');
      const formattedJson = ref('');
      const showSnackbar = ref(false);
      const isTreeView = ref(false);
      const parsedJson = ref({});
      const showModal = ref(false);
  
      const formatJSON = () => {
        try {
          const obj = JSON.parse(jsonInput.value);
          parsedJson.value = obj;
          formattedJson.value = JSON.stringify(obj, null, 2);
        } catch (error) {
          formattedJson.value = 'Invalid JSON';
          parsedJson.value = {};
        }
      };
  
      const toggleView = () => {
        isTreeView.value = !isTreeView.value;
      };
  
      const copyToClipboard = async (text: string) => {
        try {
          await navigator.clipboard.writeText(text);
          showSnackbar.value = true;
          setTimeout(() => { showSnackbar.value = false; }, 3000); // Hide after 3 seconds
        } catch (err) {
          console.error('Failed to copy text: ', err);
        }
      };
  
      return { 
        jsonInput, 
        formattedJson, 
        formatJSON, 
        copyToClipboard, 
        showSnackbar,
        isTreeView,
        toggleView,
        parsedJson,
        showModal
      };
    }
  });
  </script>
  
  <style scoped>
.json-output {
  white-space: pre-wrap;
  word-wrap: break-word;
}
/* Scrollbar styling for modal */
.modal-content::-webkit-scrollbar {
  width: 8px;
}
.modal-content::-webkit-scrollbar-track {
  @apply bg-slate-900;
}
.modal-content::-webkit-scrollbar-thumb {
  @apply bg-slate-700 rounded-full;
}
</style>
  